import { Box, IconButton, TableCell, TableRow } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ROLE_LABELS, type DashboardUserInvite } from "@xop/dashboard-shared";
import { Check, Mail, Trash2 } from "lucide-react";
import { useState } from "react";
import type { JSX } from "react";

import { UserAvatar } from "../../../components/UserAvatar.js";
import { ApiError } from "../../../lib/api.js";

export function InviteRow({
  invite,
  onCancel,
  onResend,
}: {
  invite: DashboardUserInvite;
  onCancel: () => void;
  onResend: () => Promise<unknown>;
}): JSX.Element {
  const [resendState, setResendState] = useState<
    "idle" | "sending" | "sent" | "error"
  >("idle");
  const [resendError, setResendError] = useState<string | null>(null);

  async function handleResend() {
    if (resendState === "sending") return;
    setResendState("sending");
    setResendError(null);
    try {
      await onResend();
      setResendState("sent");
      setTimeout(() => setResendState("idle"), 3000);
    } catch (err) {
      setResendState("error");
      setResendError(
        err instanceof ApiError
          ? err.message
          : "Could not send the invite email.",
      );
    }
  }

  return (
    <TableRow sx={{ bgcolor: "surfaceSoft" }}>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          {/* userId 0 → initials-only (no account yet), seeded from the
              invited name or email so the row already reads as a person. */}
          <UserAvatar userId={0} name={invite.name ?? invite.email} size={32} />
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Box component="span" sx={{ fontWeight: 500 }}>
                {invite.name ?? invite.email}
              </Box>
              <Box
                component="span"
                sx={{
                  borderRadius: 999,
                  border: 1,
                  borderColor: "divider",
                  bgcolor: "background.default",
                  px: 1,
                  py: 0.25,
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: "text.disabled",
                }}
              >
                Pending sign-in
              </Box>
            </Box>
            {invite.name && (
              <Box sx={{ fontSize: 12, color: "text.disabled" }}>{invite.email}</Box>
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
        {ROLE_LABELS[invite.dashboard_role]}
      </TableCell>
      <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
        {invite.permissions.length} granted
      </TableCell>
      <TableCell sx={{ fontSize: 12, color: "text.disabled" }}>—</TableCell>
      <TableCell sx={{ fontSize: 12, color: "text.disabled" }}>
        Invited {new Date(invite.created_at).toLocaleDateString()}
        {invite.invited_by_email ? ` by ${invite.invited_by_email}` : ""}
      </TableCell>
      <TableCell sx={{ textAlign: "right" }}>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
          {resendState === "error" && resendError ? (
            <Box
              component="span"
              title={resendError}
              sx={{
                mr: 0.5,
                maxWidth: 180,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: 11,
                color: "error.main",
              }}
            >
              {resendError}
            </Box>
          ) : resendState === "sent" ? (
            <Box component="span" sx={{ mr: 0.5, fontSize: 11, fontWeight: 500, color: "success.main" }}>
              Invite sent
            </Box>
          ) : null}
          <IconButton
            onClick={handleResend}
            disabled={resendState === "sending"}
            title={`Resend invite to ${invite.email}`}
            aria-label={`Resend invite to ${invite.email}`}
            sx={{
              color: "text.disabled",
              "&:hover": {
                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                color: "primary.main",
              },
            }}
          >
            {resendState === "sent" ? (
              <Box component="span" sx={{ display: "inline-flex", color: "success.main" }}>
                <Check size={16} />
              </Box>
            ) : (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  ...(resendState === "sending"
                    ? {
                        animation: "dashUsersPulse 1.5s ease-in-out infinite",
                        "@keyframes dashUsersPulse": {
                          "0%, 100%": { opacity: 1 },
                          "50%": { opacity: 0.4 },
                        },
                      }
                    : {}),
                }}
              >
                <Mail size={16} />
              </Box>
            )}
          </IconButton>
          <IconButton
            onClick={onCancel}
            title={`Cancel invite for ${invite.email}`}
            aria-label={`Cancel invite for ${invite.email}`}
            sx={{
              color: "text.disabled",
              "&:hover": {
                bgcolor: (t) => alpha(t.palette.error.main, 0.12),
                color: "error.main",
              },
            }}
          >
            <Trash2 size={16} />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );
}
